import { useState, useCallback, useEffect } from 'react';
import { Box, Sheet, Typography, Button, IconButton, Divider, CircularProgress, Snackbar } from '@mui/joy';
import { Plus as Add, FloppyDisk as Save, FloppyDisk as SaveAs, ArrowLeft as ArrowBack } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pagesApi } from '../../lib/apiPages';
import { PageEditor } from './components/PageEditor';
import { SidebarTree } from './components/SidebarTree';
import type { Page } from '../../types/pages';
import { FeatureGate } from '../../components/common/FeatureGate';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';

function PagesPageContent() {
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 959.95px)').matches : false
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 959.95px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // On mobile, hide sidebar when a page is selected; show only editor.
  // "Back" button returns to sidebar.
  const showSidebar = !isMobile || !selectedPageId;
  const showEditor = !isMobile || !!selectedPageId;

  // Fetch page tree
  const { data: pageTree, isLoading: isTreeLoading } = useQuery({
    queryKey: ['pages', 'tree'],
    queryFn: pagesApi.getTree,
  });

  // Fetch selected page details
  const { data: selectedPage, isLoading: isPageLoading } = useQuery({
    queryKey: ['pages', selectedPageId],
    queryFn: () => selectedPageId ? pagesApi.getPage(selectedPageId) : null,
    enabled: !!selectedPageId,
  });

  // Create page mutation
  const createPageMutation = useMutation({
    mutationFn: pagesApi.createPage,
    onSuccess: (newPage) => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      setSelectedPageId(newPage.id);
      setSnackbar({ open: true, message: 'Page created successfully' });
    },
  });

  // Update page mutation with optimistic updates
  const updatePageMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Page> }) =>
      pagesApi.updatePage(id, data),
    onMutate: async ({ id, data }) => {
      setSaveStatus('saving');

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['pages', id] });

      // Snapshot the previous value
      const previousPage = queryClient.getQueryData(['pages', id]);

      // Optimistically update the cache
      queryClient.setQueryData(['pages', id], (old: Page | undefined) =>
        old ? { ...old, ...data } : old
      );

      return { previousPage };
    },
    onSuccess: (_data, variables) => {
      setSaveStatus('saved');
      // Only invalidate the tree for sidebar updates (title changes)
      // DON'T invalidate the page itself - we already have the correct local state
      if (variables.data.title !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['pages', 'tree'] });
      }
    },
    onError: (_err, variables, context) => {
      setSaveStatus('unsaved');
      // Rollback on error
      if (context?.previousPage) {
        queryClient.setQueryData(['pages', variables.id], context.previousPage);
      }
      setSnackbar({ open: true, message: 'Failed to save page' });
    },
  });

  // Delete page mutation
  const deletePageMutation = useMutation({
    mutationFn: pagesApi.deletePage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      setSelectedPageId(null);
      setSnackbar({ open: true, message: 'Page deleted successfully' });
    },
  });

  // Handle creating a new page
  const handleCreatePage = (parentId?: string) => {
    const newPage = {
      title: 'New Page',
      parent: parentId || null,
      content: {
        type: 'doc',
        content: [{ type: 'paragraph' }],
      },
      is_published: true,
      position: 0,
    };
    createPageMutation.mutate(newPage);
  };

  // Handle page content update with debounce
  const handlePageUpdate = useCallback(
    (updates: Partial<Page>) => {
      if (selectedPageId) {
        setSaveStatus('unsaved');
        updatePageMutation.mutate({ id: selectedPageId, data: updates });
      }
    },
    [selectedPageId, updatePageMutation]
  );

  // Handle page deletion
  const handleDeletePage = (pageId: string) => {
    if (window.confirm('Are you sure you want to delete this page and all its children?')) {
      deletePageMutation.mutate(pageId);
    }
  };

  // Handle page reordering
  const handleReorderPages = async (items: any[]) => {
    try {
      await pagesApi.reorderPages(items);
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      setSnackbar({ open: true, message: 'Pages reordered successfully' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to reorder pages' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Sheet
          sx={{
            width: { xs: '100%', md: 300 },
            display: showSidebar ? 'flex' : 'none',
            borderRight: { xs: 0, md: 1 },
            borderColor: 'divider',
            overflow: 'hidden',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="h4">Pages</Typography>
              <PageHelpButton />
            </Box>
            <Button
              size="sm"
              startDecorator={<Add />}
              onClick={() => handleCreatePage()}
            >
              New Page
            </Button>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {isTreeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <SidebarTree
                tree={pageTree || []}
                selectedPageId={selectedPageId}
                onSelectPage={setSelectedPageId}
                onCreatePage={handleCreatePage}
                onDeletePage={handleDeletePage}
                onReorderPages={handleReorderPages}
              />
            )}
          </Box>
        </Sheet>

        {/* Main Editor Area */}
        <Box sx={{ flex: 1, display: showEditor ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', width: { xs: '100%', md: 'auto' } }}>
          {selectedPage ? (
            <>
              {/* Save Status Indicator */}
              <Box sx={{ px: { xs: 1.5, md: 3 }, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}>
                {isMobile && (
                  <IconButton size="sm" variant="plain" onClick={() => setSelectedPageId(null)} aria-label="Back to pages">
                    <ArrowBack />
                  </IconButton>
                )}
                {saveStatus === 'saving' && (
                  <>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Saving...</Typography>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <Save color="var(--joy-palette-success-500)" />
                    <Typography level="body-sm" color="success">Saved</Typography>
                  </>
                )}
                {saveStatus === 'unsaved' && (
                  <>
                    <SaveAs color="var(--joy-palette-warning-500)" />
                    <Typography level="body-sm" color="warning">Unsaved changes</Typography>
                  </>
                )}
              </Box>
              
              <PageEditor
                page={selectedPage}
                onUpdate={handlePageUpdate}
                isLoading={isPageLoading}
              />
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
                color: 'text.secondary',
              }}
            >
              <Typography level="h3">No page selected</Typography>
              <Typography>Select a page from the sidebar or create a new one</Typography>
              <Button
                size="lg"
                startDecorator={<Add />}
                onClick={() => handleCreatePage()}
              >
                Create Your First Page
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
}

export function PagesPage() {
  return (
    <FeatureGate
      requiredTiers={['pro', 'premium', 'enterprise']}
      featureName="Knowledge Base & Wiki"
      featureDescription="Create and manage a comprehensive knowledge base for your team with rich content editing, organization, and collaboration features."
      benefits={[
        'Create unlimited pages and documentation',
        'Rich text editor with formatting options',
        'Hierarchical page organization',
        'Real-time collaboration and editing',
        'Version history and change tracking',
        'Search and navigation features',
        'Export and sharing capabilities'
      ]}
    >
      <PagesPageContent />
    </FeatureGate>
  );
}